import type { SerializeFrom } from '@remix-run/server-runtime'
import { GripVerticalIcon } from 'lucide-react'
import { Reorder } from 'motion/react'
import { useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Media } from '~/components/Media/Media'
import { ShopReorderContext } from '~/components/ShopReorder'
import { SideDrawer } from '~/components/SideDrawer'
import { getProductDisplayPrice } from '~/helpers/product'
import { makeShopOwnPath } from '~/helpers/routes'
import { getEntireStoreDiscount } from '~/helpers/shop'
import { useShop } from '~/hooks/data/use-shop'
import type { ProductFull } from '~/models/product.server'
import { Alert } from '~/ui'
import { centsToEurosFormattedPrice } from '~/utils/number'
import { getProductCoverMedia } from '~/utils/product'

const ProductsReorderDrawerContent = () => {
  const { t } = useTranslation()
  const shop = useShop()
  const { open, setOpen, products, updateProducts, resetProducts } =
    useContext(ShopReorderContext)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const orderedProducts = useMemo(() => products, [products])

  const handleReorder = async (newOrder: string[]) => {
    const reorderedProducts = newOrder
      .map((id) => orderedProducts.find((p) => p.id === id)!)
      .filter(Boolean)

    const result = reorderedProducts.map((p, index) => ({
      id: p.id,
      disposition: reorderedProducts.length - index,
    }))

    updateProducts(reorderedProducts)

    try {
      const response = await fetch(makeShopOwnPath(shop, '/products/reorder'), {
        method: 'PATCH',
        body: JSON.stringify({ products: result }),
      })
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      await response.json()
    } catch (error) {
      console.error(error)
      resetProducts()
      setErrorMessage(t('errors.reorder_products_error'))
    }
  }

  return (
    <SideDrawer
      open={open === 'products'}
      onOpenChange={(isOpen) => setOpen(isOpen ? 'products' : null)}
      className="text-foreground"
    >
      <SideDrawer.Header
        title={t('action.reorder_products')}
        description={t('action.reorder_products')}
      />
      <SideDrawer.Content>
        {errorMessage && (
          <Alert className="mb-3" type="error" title={errorMessage} />
        )}

        <Reorder.Group
          axis="y"
          values={orderedProducts.map((p) => p.id)}
          onReorder={handleReorder}
          className="flex list-none flex-col gap-2"
        >
          {orderedProducts.map((product) => (
            <Reorder.Item
              key={product.id}
              value={product.id}
              className="shrink-0"
            >
              <ProductReorderItem product={product} />
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </SideDrawer.Content>
    </SideDrawer>
  )
}

export const ProductsReorderDrawer = () => {
  const context = useContext(ShopReorderContext)

  if (!context) {
    return null
  }

  return <ProductsReorderDrawerContent />
}

const ProductReorderItem = ({
  product,
}: {
  product: SerializeFrom<ProductFull>
}) => {
  const shop = useShop()
  const shopDiscount = useMemo(() => getEntireStoreDiscount(shop), [shop])

  const price = useMemo(
    () => ({
      current: getProductDisplayPrice(product, shopDiscount || null) || 0,
      previous: shopDiscount ? getProductDisplayPrice(product, null) : null,
    }),
    [shopDiscount, product]
  )

  const media = useMemo(() => getProductCoverMedia(product), [product])

  return (
    <div className="bg-background border-border/4 flex items-center gap-2.5 rounded-xl border p-4 hover:cursor-move">
      {media && (
        <div className="size-11 shrink-0 overflow-hidden rounded-md">
          <Media
            src={media.src}
            kind={media.kind}
            transformation={{
              width: 100,
              format: 'webp',
              resizeMode: 'fill',
            }}
            sizes={{
              default: '45px',
            }}
          />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="line-clamp-1 truncate text-sm font-medium">
          {product.name}
        </p>
        <p className="text-xs opacity-60">
          <span>{centsToEurosFormattedPrice(price.current)}</span>
          {price.previous !== null && (
            <span className="ml-1.5 line-through opacity-64">
              {centsToEurosFormattedPrice(price.previous)}
            </span>
          )}
        </p>
      </div>

      <GripVerticalIcon className="ml-auto size-3 shrink-0 opacity-40" />
    </div>
  )
}
